import { FC } from 'react';
import {
  Drawer,
  Button,
  Stack,
  Text,
  Group,
  Loader,
  ScrollArea,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { usePatientNotes } from './usePatientNotes';
import { NoteItem } from './NoteItem';

// -----------------------------------------------------------------------------
interface PatientNotesDrawerProps {
  opened: boolean;
  onClose: () => void;
}

// -----------------------------------------------------------------------------
export const PatientNotesDrawer: FC<PatientNotesDrawerProps> = ({
  opened,
  onClose,
}) => {
  const { t } = useTranslation(['patient-pages', 'common']);

  const {
    notes,
    isLoading,
    isSaving,
    isCreatingNew,
    editingNoteId,
    editingContent,
    startNewNote,
    startEditingNote,
    updateEditingContent,
    stopEditing,
    deleteNote,
  } = usePatientNotes();

  // ---------------------------------------------------------------------------
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600}>
          {t('notes.title', {
            ns: 'patient-pages',
            defaultValue: 'Patient Notes',
          })}
        </Text>
      }
      position="right"
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {/* Add New Note Button */}
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={startNewNote}
          fullWidth
          variant="light"
          disabled={isCreatingNew}
        >
          {t('notes.addNew', {
            ns: 'patient-pages',
            defaultValue: 'Add New Note',
          })}
        </Button>

        {/* Loading State */}
        {isLoading ? (
          <Group justify="center" p="xl">
            <Loader size="md" />
          </Group>
        ) : (
          <Stack gap="sm">
            {/* All Notes (including new note if creating) */}
            {notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                isEditing={editingNoteId === note.id}
                isSaving={isSaving && editingNoteId === note.id}
                editingContent={
                  editingNoteId === note.id ? editingContent : undefined
                }
                onContentChange={updateEditingContent}
                onStartEditing={() => note.id && startEditingNote(note.id)}
                onStopEditing={stopEditing}
                onDelete={() => note.id && deleteNote(note.id)}
              />
            ))}

            {/* Empty State */}
            {notes.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" p="xl">
                {t('notes.empty', {
                  ns: 'patient-pages',
                  defaultValue:
                    'No notes yet. Click "Add New Note" to create one.',
                })}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};
